/**
 * <script src="https://pageid.app/label/pageid-label.js" async></script>
 * <pageid-label customer="projekt-y"></pageid-label>
 *
 * The PageID pill (SPEC.md §12.3): wherever the `<pageid-label>` tag sits, ONE frame from PageID's own domain
 * (`/label/<customer>?url=<this page>`) appears fixed at the top of the page: a pill that slides down, shows the
 * reader's own symbol and phrase when the site is verified, and then becomes a small round icon (top right).
 *
 * Only that frame can read the reader's PageID cookie, so this page (and any script on it) can never read or
 * draw the reader's real symbol or phrase. The frame tells this script only which box it needs (`pill` /
 * `icon` / `stage` / `hidden` and a size, origin-checked); this script tells the frame only the viewport width
 * and the right safe-area inset. Nothing else crosses the boundary.
 *
 * Attributes (on the first `<pageid-label>` of the page): `customer` (required), `page` (default: this page's
 * address without the fragment), `server` (default: where this script came from), `demo` (PageID's own demo
 * page only). The element itself stays empty: it is the mount point and the configuration.
 */
import { boxFor, parseFrameMessage, type FrameMessage, type HostMessage } from './pill-logic.ts';

(() => {
  const script = document.currentScript as HTMLScriptElement | null;
  // PageID moved from netid.dev-y.com to pageid.app (2026-10-26). An old embed's script tag still names the old
  // host and reaches this script through a 301; `currentScript.src` keeps the old address, so map it here: the
  // frame then loads from pageid.app directly and its messages come from the origin we expect.
  const MOVED: Record<string, string> = { 'https://netid.dev-y.com': 'https://pageid.app' };
  const moved = (o: string) => MOVED[o] ?? o;
  const ORIGIN = moved(script?.src ? new URL(script.src).origin : location.origin);

  /** The one pill of this page (several `<pageid-label>` tags still make one pill). */
  let pill: Pill | null = null;
  const mounts = new Set<HTMLElement>();

  class Pill {
    readonly host: HTMLElement;
    readonly frame: HTMLIFrameElement;
    readonly server: string;
    private probe: HTMLElement;
    private last: FrameMessage | null = null;

    constructor(el: HTMLElement) {
      let server = ORIGIN;
      try {
        server = moved(new URL(el.getAttribute('server') ?? ORIGIN).origin);
      } catch {}
      this.server = server;
      const page = new URL(el.getAttribute('page') ?? location.href, location.href);
      page.hash = '';
      // An undefined custom element name: generic page CSS (div, iframe, [popover]) is unlikely to touch it,
      // and every property that matters is set inline with !important.
      this.host = document.createElement('pageid-pill');
      const root = this.host.attachShadow({ mode: 'closed' });
      const f = document.createElement('iframe');
      f.src = el.hasAttribute('demo') ? `${server}/label/_demo` : `${server}/label/${encodeURIComponent(el.getAttribute('customer') ?? '')}?url=${encodeURIComponent(page.toString())}`;
      f.title = 'PageID: which verified company runs this page';
      // The frame request must carry this page's origin, whatever the page's own referrer policy.
      f.referrerPolicy = 'origin';
      f.setAttribute('allow', 'storage-access');
      f.setAttribute('scrolling', 'no');
      this.probe = document.createElement('span');
      root.innerHTML = '<style>:host{all:initial}iframe{display:block;border:0;width:100%;height:100%;background:transparent;color-scheme:light;overflow:hidden}span{position:fixed;top:0;left:0;width:0;height:0;padding-right:env(safe-area-inset-right,0px);visibility:hidden;pointer-events:none}</style>';
      root.append(f, this.probe);
      // The viewport width before the frame draws anything (a refused frame never accepts it: wrong origin).
      f.addEventListener('load', () => this.tellFrame());
      this.frame = f;
      this.style({
        position: 'fixed', inset: 'auto', top: 'env(safe-area-inset-top, 0px)', left: '0px', width: '100%', height: '80px',
        margin: '0', padding: '0', border: '0', background: 'transparent', overflow: 'visible', 'max-width': 'none', 'max-height': 'none',
        // Hidden until the frame speaks: a frame the browser refused (CSP frame-ancestors) never shows an error box.
        visibility: 'hidden', 'pointer-events': 'none', 'z-index': '2147483647', transform: 'none', opacity: '1', filter: 'none',
        contain: 'layout size', display: 'block', 'box-sizing': 'border-box',
      });
      f.style.setProperty('pointer-events', 'none', 'important');
      this.host.setAttribute('popover', 'manual');
      document.documentElement.append(this.host);
      // The top layer: above everything the page stacks with z-index, and immune to transformed ancestors.
      try {
        (this.host as HTMLElement & { showPopover?: () => void }).showPopover?.();
      } catch {}
      window.addEventListener('message', this.onMessage);
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    private style(p: Record<string, string>) {
      for (const [k, v] of Object.entries(p)) this.host.style.setProperty(k, v, 'important');
    }

    private vw = () => document.documentElement.clientWidth || window.innerWidth;
    private ir = () => parseFloat(getComputedStyle(this.probe).paddingRight) || 0;

    private tellFrame() {
      const m: HostMessage = { pageidHost: 1, vw: this.vw(), ir: this.ir() };
      this.frame.contentWindow?.postMessage(m, this.server);
    }

    private apply(m: FrameMessage) {
      this.last = m;
      if (m.mode === 'hidden') {
        this.style({ visibility: 'hidden', 'pointer-events': 'none' });
        this.frame.style.setProperty('pointer-events', 'none', 'important');
        return;
      }
      const vw = this.vw();
      // The box's position comes from this page's own geometry; its size from the frame, never wider than the viewport.
      const b = boxFor(m.mode, vw, this.ir(), 48);
      const w = Math.min(Math.round(m.w), vw);
      const left = m.mode === 'stage' ? 0 : m.mode === 'icon' ? b.left : Math.floor((vw - w) / 2);
      this.style({ visibility: 'visible', left: `${left}px`, width: `${w}px`, height: `${Math.round(m.h)}px` });
      // The morph runs in the whole top strip: it must never take the page's clicks.
      this.frame.style.setProperty('pointer-events', m.mode === 'stage' ? 'none' : 'auto', 'important');
    }

    private onMessage = (e: MessageEvent) => {
      if (e.source !== this.frame.contentWindow || e.origin !== this.server) return;
      const m = parseFrameMessage(e.data);
      if (!m) return;
      const first = !this.last;
      this.apply(m);
      if (first) this.tellFrame();
    };

    private onResize = () => {
      this.tellFrame();
      if (this.last) this.apply(this.last);
    };

    remove() {
      window.removeEventListener('message', this.onMessage);
      window.removeEventListener('resize', this.onResize);
      this.host.remove();
    }
  }

  class PageIdLabel extends HTMLElement {
    connectedCallback() {
      mounts.add(this);
      if (!pill) pill = new Pill(this);
    }

    disconnectedCallback() {
      mounts.delete(this);
      // The last tag left the page (a single-page app navigated away): the pill goes with it.
      queueMicrotask(() => {
        if (!mounts.size && pill) {
          pill.remove();
          pill = null;
        }
      });
    }
  }
  if (!customElements.get('pageid-label')) customElements.define('pageid-label', PageIdLabel);
})();
